import React, { useEffect, useRef, useState } from 'react';
import Sheet, { PILL_BASE, pill } from './Sheet';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '../canvas/icons';
import {
  DOW, resolve, withStart, withEnd, windowLabel, windowHelp, dayName, endName, fmtH, startHours,
  stripDays, calendarCells, monthOffsetOf, CALENDAR_MONTHS, DEFAULT_START_HOUR,
} from '../../../utils/portalWindow';

/**
 * WHEN — the window sheet (handoff v3 §5.5). A switch, then DAY (a strip of two weeks and an
 * inline month calendar under PICK A DATE — never `showPicker()`, which is blocked in iframes),
 * STARTS (hour pills, NOW today), and ENDS (its own strip and calendar, and an hour row that
 * scrolls to the first enabled hour whenever the start moves).
 */
const DOW_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const label10 = { fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.6)' };
const rowStyle = { display: 'flex', gap: 6, overflowX: 'auto', margin: '0 -16px', padding: '0 16px' };

function Calendar({ monthOffset, onMonth, selected, minDay, onPick, now }) {
  const { title, cells } = calendarCells(monthOffset, { selected, minDay, now });
  return (
    <div data-testid="calendar" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: 'rgba(255,255,255,.07)', borderRadius: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button type="button" aria-label="Previous month" onClick={() => onMonth(Math.max(0, monthOffset - 1))} style={{ ...PILL_BASE, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: 'rgba(255,255,255,.12)', color: '#fff', borderRadius: '50%', opacity: monthOffset > 0 ? 1 : 0.3 }}><ChevronLeftIcon size={14} /></button>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em' }}>{title}</span>
        <button type="button" aria-label="Next month" onClick={() => onMonth(Math.min(CALENDAR_MONTHS, monthOffset + 1))} style={{ ...PILL_BASE, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, background: 'rgba(255,255,255,.12)', color: '#fff', borderRadius: '50%' }}><ChevronRightIcon /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 2, justifyItems: 'center' }}>
        {DOW_SHORT.map((w, i) => <span key={i} style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: 'rgba(255,255,255,.5)', padding: '2px 0' }}>{w}</span>)}
        {cells.map((c) => (c.blank ? <span key={c.key} aria-hidden style={{ width: 34, height: 34 }} /> : (
          <button
            key={c.key}
            type="button"
            disabled={c.past}
            aria-pressed={c.selected}
            aria-label={`Day ${c.n}`}
            onClick={() => onPick(c.index)}
            style={{ width: 34, height: 34, padding: 0, background: c.selected ? '#fff' : c.today ? 'rgba(0,255,255,.18)' : 'transparent', color: c.selected ? '#000' : '#fff', border: 0, borderRadius: '50%', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: c.past ? 'default' : 'pointer', opacity: c.past ? 0.3 : 1 }}
          >
            {c.n}
          </button>
        )))}
      </div>
    </div>
  );
}

function DayStrip({ days, chosen, onPick, now, testId }) {
  return (
    <div className="prc-row" style={rowStyle} data-testid={testId}>
      {days.map((i) => {
        const d = new Date(now); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + i);
        return (
          <button key={i} type="button" aria-pressed={i === chosen} onClick={() => onPick(i)} style={{ ...PILL_BASE, flex: '0 0 48px', height: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: 0, ...pill(i === chosen) }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em' }}>{i === 0 ? 'TODAY' : DOW[d.getDay()]}</span>
            <span style={{ fontSize: 15, fontWeight: 700 }}>{d.getDate()}</span>
          </button>
        );
      })}
    </div>
  );
}

function DateButton({ open, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} style={{ ...PILL_BASE, display: 'flex', alignItems: 'center', gap: 6, height: 32, padding: '0 14px', background: '#fff', color: '#000', fontSize: 10, letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>
      <CalendarIcon />{open ? 'CLOSE' : 'PICK A DATE'}
    </button>
  );
}

export default function WindowSheet({ value, onChange, onClose, now: nowProp = null }) {
  const now = nowProp || new Date();
  const w = value;
  const r = resolve(w, now);
  const [cal, setCal] = useState(null);          // null | 'start' | 'end'
  const [calM, setCalM] = useState(0);
  const endRow = useRef(null);
  const endTapped = useRef(false);

  const set = (next) => onChange({ ...w, ...next });
  const toggleCal = (which) => {
    if (cal === which) { setCal(null); return; }
    setCal(which);
    setCalM(monthOffsetOf(which === 'end' ? r.eDay : r.day, now));
  };

  // When the start or the day changes, the end-time row scrolls so the first enabled hour sits
  // at the left. Not when the end itself was tapped.
  useEffect(() => {
    if (!w.on) return undefined;
    if (endTapped.current) { endTapped.current = false; return undefined; }
    const raf = requestAnimationFrame(() => {
      const row = endRow.current;
      if (!row) return;
      const first = [...row.querySelectorAll('button')].find((b) => !b.disabled);
      if (first) row.scrollTo?.({ left: Math.max(0, first.offsetLeft - 16), behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [w.on, w.day, w.start, w.endAbs]);

  const days = stripDays(0, r.day);
  const endDays = stripDays(r.day, r.eDay);
  const hours = startHours(r.day, now);
  const startOpts = [...(r.day === 0 ? [{ label: 'NOW', v: null }] : []), ...hours.map((x) => ({ label: fmtH(x), v: x }))];

  return (
    <Sheet title="WHEN" onClose={onClose} testId="window-sheet">
      <button type="button" role="switch" aria-checked={!!w.on} aria-label="Give it a window" onClick={() => set({ on: !w.on })} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 0, background: 'transparent', color: '#fff', border: 0, fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em' }}>GIVE IT A WINDOW</span>
          <span style={{ fontSize: 10, lineHeight: 1.5, color: 'rgba(255,255,255,.65)' }} data-testid="window-help">{windowHelp(w, now)}</span>
        </span>
        <span aria-hidden style={{ position: 'relative', flexShrink: 0, width: 48, height: 28, borderRadius: 999, background: w.on ? '#00FFFF' : 'rgba(255,255,255,.2)', transition: 'background-color .2s' }}>
          <span style={{ position: 'absolute', top: 3, left: w.on ? 23 : 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
        </span>
      </button>

      {w.on && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={label10}>DAY · <span style={{ color: '#fff' }}>{dayName(r.day, now)}</span></span>
            <DateButton open={cal === 'start'} onClick={() => toggleCal('start')} testId="pick-start-date" />
          </div>
          {cal === 'start' && (
            <Calendar monthOffset={calM} onMonth={setCalM} selected={r.day} minDay={0} now={now} onPick={(i) => { setCal(null); onChange(withStart(w, i, i === 0 ? null : (w.start ?? DEFAULT_START_HOUR), now)); }} />
          )}
          <DayStrip days={days} chosen={r.day} now={now} testId="day-strip" onPick={(i) => onChange(withStart(w, i, i === 0 ? null : (w.start ?? DEFAULT_START_HOUR), now))} />

          <span style={label10}>STARTS · <span style={{ color: '#fff' }}>{r.start == null ? 'NOW' : fmtH(r.start)}</span></span>
          <div className="prc-row" style={rowStyle} data-testid="start-row">
            {startOpts.map((o) => (
              <button key={o.label} type="button" aria-pressed={r.start === o.v} onClick={() => onChange(withStart(w, r.day, o.v, now))} style={{ ...PILL_BASE, flex: '0 0 auto', height: 34, padding: '0 14px', ...pill(r.start === o.v), fontSize: 11, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{o.label}</button>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={label10}>ENDS · <span style={{ color: '#fff' }} data-testid="end-name">{endName(w, now)}</span></span>
            <DateButton open={cal === 'end'} onClick={() => toggleCal('end')} testId="pick-end-date" />
          </div>
          {cal === 'end' && (
            <Calendar monthOffset={calM} onMonth={setCalM} selected={r.eDay} minDay={r.day} now={now} onPick={(i) => { setCal(null); onChange(withEnd(w, i, null, now)); }} />
          )}
          <DayStrip days={endDays} chosen={r.eDay} now={now} testId="end-day-strip" onPick={(i) => onChange(withEnd(w, i, null, now))} />
          <div ref={endRow} className="prc-row" style={rowStyle} data-testid="end-row">
            {Array.from({ length: 24 }, (_, x) => x).map((x) => {
              const abs = r.eDay * 24 + x;
              const off = abs <= r.startAbs;
              return (
                <button key={x} type="button" disabled={off} aria-pressed={x === r.eHour} onClick={() => { endTapped.current = true; set({ endAbs: abs }); }} style={{ ...PILL_BASE, flex: '0 0 auto', height: 34, padding: '0 14px', whiteSpace: 'nowrap', ...pill(x === r.eHour), opacity: off ? 0.3 : 1, fontSize: 11, letterSpacing: '0.08em', cursor: off ? 'default' : 'pointer' }}>{fmtH(x)}</button>
              );
            })}
          </div>
        </>
      )}

      <button type="button" onClick={onClose} data-testid="window-done" style={{ ...PILL_BASE, marginTop: 4, height: 44, background: '#fff', color: '#000', fontSize: 12, letterSpacing: '0.14em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {w.on ? `SET · ${windowLabel(w, now)}` : 'DONE'}
      </button>
    </Sheet>
  );
}
